import {useState} from 'react';
import {sanitize} from '../../helpers';
import RequestError from '../../api/RequestError';
import {handleApiValidationErrors} from '../../helpers/error';
import {Form, Formik, FormikHelpers} from 'formik';
import {Button} from '../common/Button';
import {updateUserEmail} from '../../api/users';
import {
  currentUserProps
} from '../../store/currentUser/currentUser.slice';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import {editUserEmailValidationSchema} from '../../constants/validationSchemes';
import {EditUserEmailProps} from '../../constants/types';
import Spinner from '../common/Spinner';
import {useAppSelector} from '../../store/hooks';
import {RootState} from '../../store/store';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {TextField} from '../common/formik/fields';
import SuccessPopup from '../common/popup/SuccessPopup';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {useSnackbarHook} from '../../hooks/useSnackbar';

enum Field {
  EMAIL = 'email'
}

const EditUserEmail = () => {
  const {t} = useTranslation('user');
  const [popupText, setPopupText] = useState<Nullable<string>>(null);
  const [apiValidationError, setApiValidationError] =
    useState<Nullable<Dictionary<string[]>>>(null);
  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );
  const {email} = currentUser || {};
  const {addErrorToast} = useSnackbarHook();

  const clearMessages = () => {
    setApiValidationError(null);
  };

  const initialValues = {
    email: email || ''
  };

  const closeModal = () => {
    setPopupText(null);
  };

  const onSubmit = async (
      values: EditUserEmailProps,
      actions: FormikHelpers<EditUserEmailProps>) => {
    const sanitizeValues = sanitize(values);

    try {
      await updateUserEmail(sanitizeValues);
      actions.resetForm();
      setPopupText(t(
          'profile.popups.emailChangeSuccessMsg',
          {email: sanitizeValues.email}
      ));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'user');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  return (
    <div className="tab-content">
      <Formik
        initialValues={initialValues}
        validationSchema={editUserEmailValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({submitForm, dirty, isSubmitting, isValid}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <Form className="form">
                <FormGroup>
                  <TextField
                    name={Field.EMAIL}
                    label={t('form.fields.email.label')}
                    disabled={isSubmitting}
                    required
                    onFocus={() => setApiValidationError(null)}
                    onBlur={() => clearMessages()}
                    apiValidationError={apiValidationError}
                  />
                </FormGroup>
              </Form>

              <div className="buttons">
                <EnsurePermission
                  elementPermissions={permissionsInfo.users.profile.edit}>
                  <Button
                    disabled={!(isValid && dirty) || isSubmitting}
                    label={t('profile.buttons.updateEmail')}
                    onClick={submitForm}>
                    {isSubmitting ? <Spinner embedded /> : null}
                  </Button>
                </EnsurePermission>
              </div>
            </>
          );
        }}
      </Formik>

      <SuccessPopup
        labels={{body: popupText || ''}}
        isOpen={!!popupText}
        setIsOpen={closeModal} />
    </div>
  );
};

export default EditUserEmail;
